/**
 * CosmicBackdrop — shared royal-purple celestial backdrop.
 *
 * Layers (back → front):
 *   1. Deep royal purple base (#12051F)
 *   2. Radial violet glow behind the focal area
 *   3. Faint gold orbital arcs (SVG, slowly rotating)
 *   4. Deterministic golden-angle starfield (white + soft-gold)
 *   5. Subtle nebula haze
 *   6. Inset vignette edges
 *
 * Phase W (perf):
 *   • Static base (layers 1, 2, 3, 5, 6) renders synchronously — cheap,
 *     no JS-driven elements, paints with first content.
 *   • Starfield (layer 4) and orbital arcs (also SVG) are deferred behind
 *     `requestIdleCallback` so the hero's first paint isn't blocked by
 *     ~80 <circle> nodes + animation styles.
 *
 * variant:
 *   "dramatic" — Landing / hero / unlock pages (full intensity)
 *   "calm"     — Dashboard / forms / readable surfaces (muted intensity)
 *   "form"     — Auth + birth-form (very calm, no orbital arcs)
 */
import { useEffect, useMemo, useState } from "react";

const PALETTE = {
    plum:      "rgba(109, 53, 168, 0.32)",
    imperial:  "rgba(75, 30, 114, 0.28)",
    royal:     "rgba(42, 15, 69, 0.55)",
    gold:      "rgba(212, 175, 55, 0.10)",
    goldStar:  "#FFE9A6",
    whiteStar: "#F8F5FF",
};

function Starfield({ count = 80, opacity = 1 }) {
    const stars = useMemo(() => Array.from({ length: count }).map((_, i) => {
        const a = i * 137.508;                                  // golden angle
        const r = 30 + ((i * 7) % 65);
        const x = 50 + r * Math.cos((a * Math.PI) / 180);
        const y = 45 + r * Math.sin((a * Math.PI) / 180) * 0.65;
        const sz = 0.55 + ((i * 11) % 7) / 8;
        const delay = (i % 13) * 0.55;
        const gold = i % 5 === 0;
        return { id: `s-${i}-${x.toFixed(1)}-${y.toFixed(1)}`,
                 x, y, r: sz * 0.18, delay,
                 fill: gold ? PALETTE.goldStar : PALETTE.whiteStar };
    }), [count]);
    return (
        <svg
            aria-hidden="true"
            className="absolute inset-0 w-full h-full pointer-events-none"
            preserveAspectRatio="none"
            viewBox="0 0 100 100"
            style={{ opacity }}
        >
            <style>{`
                @keyframes lsTwk {
                    0%, 100% { opacity: 0.20; }
                    50%      { opacity: 0.95; }
                }
            `}</style>
            {stars.map((s) => (
                <circle key={s.id} cx={s.x} cy={s.y} r={s.r}
                        fill={s.fill}
                        style={{ animation: `lsTwk 4.2s ease-in-out ${s.delay}s infinite` }} />
            ))}
        </svg>
    );
}

function OrbitalArcs({ opacity = 0.18 }) {
    return (
        <svg
            aria-hidden="true"
            className="absolute inset-0 w-full h-full pointer-events-none"
            preserveAspectRatio="xMidYMid slice"
            viewBox="0 0 100 100"
            style={{ opacity }}
        >
            <style>{`
                @keyframes lsOrb { to { transform: rotate(360deg); } }
                .ls-orb { transform-origin: 50% 50%; }
            `}</style>
            <ellipse cx="50" cy="50" rx="42" ry="22"
                     fill="none"
                     stroke="rgba(185,138,46,0.55)"
                     strokeWidth="0.12"
                     className="ls-orb"
                     style={{ animation: "lsOrb 280s linear infinite" }} />
            <ellipse cx="50" cy="50" rx="32" ry="38"
                     fill="none"
                     stroke="rgba(241,215,138,0.30)"
                     strokeWidth="0.10"
                     className="ls-orb"
                     style={{ animation: "lsOrb 420s linear infinite reverse" }} />
            <ellipse cx="50" cy="50" rx="48" ry="48"
                     fill="none"
                     stroke="rgba(212,175,55,0.20)"
                     strokeWidth="0.08"
                     strokeDasharray="0.4 1.2"
                     className="ls-orb"
                     style={{ animation: "lsOrb 600s linear infinite" }} />
        </svg>
    );
}

export default function CosmicBackdrop({
    variant = "dramatic",
    showMountain = false,
    className = "",
}) {
    // Per-variant intensities
    const intensity = variant === "dramatic" ? 1 : (variant === "calm" ? 0.7 : 0.5);
    const orbital = variant !== "form";
    const stars = variant === "form" ? 50 : 80;
    const starOpacity = variant === "dramatic" ? 1 : 0.7;

    // Phase W (perf): defer the SVG-heavy layers until the browser is idle so
    // first paint isn't blocked by ~80 <circle> nodes + orbital ellipses.
    const [decorReady, setDecorReady] = useState(false);
    useEffect(() => {
        if (decorReady) return undefined;
        if ("requestIdleCallback" in window) {
            const id = window.requestIdleCallback(() => setDecorReady(true), { timeout: 800 });
            return () => window.cancelIdleCallback?.(id);
        }
        const t = window.setTimeout(() => setDecorReady(true), 200);
        return () => window.clearTimeout(t);
    }, [decorReady]);

    return (
        <div
            aria-hidden="true"
            data-testid="cosmic-backdrop"
            data-variant={variant}
            className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`}
        >
            {/* L1 · Royal purple base */}
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse at top, #2A0F45 0%, #1A0830 50%, #12051F 100%)",
                }}
            />

            {/* L2 · Imperial nebula plume */}
            <div
                className="absolute -top-40 -right-32 w-[920px] h-[920px] rounded-full blur-3xl"
                style={{
                    opacity: 0.7 * intensity,
                    background:
                        `conic-gradient(from 200deg at 50% 50%, ${PALETTE.plum} 0deg, ${PALETTE.imperial} 80deg, ${PALETTE.gold} 140deg, transparent 220deg, ${PALETTE.plum} 360deg)`,
                }}
            />

            {/* L3 · Gold horizon radiant */}
            <div
                className="absolute bottom-[-200px] left-1/2 -translate-x-1/2 w-[820px] h-[480px] rounded-full blur-3xl"
                style={{
                    opacity: 0.5 * intensity,
                    background:
                        "radial-gradient(ellipse, rgba(212,175,55,0.45) 0%, transparent 70%)",
                }}
            />

            {/* L4 · Slow gold orbital arcs (deferred to idle to keep first paint cheap) */}
            {orbital && decorReady && <OrbitalArcs opacity={0.22 * intensity} />}

            {/* L5 · Starfield (deferred to idle — ~80 SVG circles) */}
            {decorReady && <Starfield count={stars} opacity={starOpacity} />}

            {/* L6 · Inset vignette */}
            <div
                className="absolute inset-0"
                style={{
                    boxShadow: "inset 0 0 220px #12051F",
                }}
            />

            {/* Optional · Mountain horizon */}
            {showMountain && (
                <div className="absolute left-0 right-0 bottom-0">
                    <svg viewBox="0 0 1600 220" preserveAspectRatio="none" className="w-full h-44 lg:h-56">
                        <defs>
                            <linearGradient id="cosmoMtn" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%"  stopColor="#2A0F45" stopOpacity="0.6" />
                                <stop offset="100%" stopColor="#12051F" stopOpacity="1" />
                            </linearGradient>
                            <linearGradient id="cosmoRim" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%"   stopColor="#D4AF37" stopOpacity="0.55" />
                                <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
                            </linearGradient>
                        </defs>
                        <path
                            d="M0 220 L0 150 L120 110 L240 130 L360 90 L500 120 L640 70 L780 105 L920 60 L1060 100 L1200 75 L1340 110 L1480 80 L1600 130 L1600 220 Z"
                            fill="url(#cosmoMtn)" opacity="0.75"
                        />
                        <path
                            d="M0 220 L0 180 L100 155 L220 175 L340 140 L460 170 L600 130 L720 165 L860 145 L1000 175 L1140 140 L1280 170 L1420 150 L1600 180 L1600 220 Z"
                            fill="#12051F"
                        />
                        <ellipse cx="800" cy="195" rx="280" ry="14" fill="url(#cosmoRim)" />
                    </svg>
                </div>
            )}
        </div>
    );
}
